/**
 * Messages between an admin page and the investor-preview iframe it embeds (same origin only).
 *
 * The iframe runs the investor build, so it renders exactly what investors see, but it holds no
 * credentials: every BackendService call it makes is forwarded to the admin page as an `rpc`
 * message and answered by the admin page's own (admin-authenticated) services.
 */

export const PREVIEW_MESSAGE_SOURCE = 'fides-investor-preview';

export type PreviewRpcRequest = {
  source: typeof PREVIEW_MESSAGE_SOURCE;
  kind: 'rpc';
  id: number;
  method: string;
  args: unknown[];
};

export type PreviewRpcResponse = {
  source: typeof PREVIEW_MESSAGE_SOURCE;
  kind: 'rpc-result';
  id: number;
  result?: unknown;
  error?: string;
};

/** Sent by the iframe whenever its content height changes, so the parent can size it. */
export type PreviewHeightMessage = {
  source: typeof PREVIEW_MESSAGE_SOURCE;
  kind: 'height';
  height: number;
};

/** Sent by the parent: what to render (e.g. the fund update page for a fund and investor). */
export type PreviewRenderMessage = {
  source: typeof PREVIEW_MESSAGE_SOURCE;
  kind: 'render';
  view: string;
  props: Record<string, unknown>;
};

/** Sent by the iframe once a requested view has rendered, so the parent can stop its spinner. */
export type PreviewRenderedMessage = {
  source: typeof PREVIEW_MESSAGE_SOURCE;
  kind: 'rendered';
};

/** Sent by the iframe once it is listening, so the parent knows when to send `render`. */
export type PreviewReadyMessage = {
  source: typeof PREVIEW_MESSAGE_SOURCE;
  kind: 'ready';
};

export type PreviewMessage =
  | PreviewRpcRequest
  | PreviewRpcResponse
  | PreviewHeightMessage
  | PreviewRenderMessage
  | PreviewRenderedMessage
  | PreviewReadyMessage;

export function isPreviewMessage(data: unknown): data is PreviewMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === PREVIEW_MESSAGE_SOURCE &&
    typeof (data as { kind?: unknown }).kind === 'string'
  );
}
